import React, { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import FilterSelect from '../components/FilterSelect';
import Pagination from '../components/Pagination';

import {
  getBookCopiesByLibrary,
  getErrorMessage
} from '../../api/api';
import '../lib-page-header.css';
import '../BookList.css';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'available', label: 'Available' },
  { value: 'issued', label: 'Issued' },
  { value: 'discarded', label: 'Discarded' }
];

const SPINE_COLORS = [
  { strong: '#2563eb', pale: '#eff6ff' },
  { strong: '#7c3aed', pale: '#f5f3ff' },
  { strong: '#0e7490', pale: '#ecfeff' },
  { strong: '#15803d', pale: '#f0fdf4' },
  { strong: '#b45309', pale: '#fffbeb' },
  { strong: '#be123c', pale: '#fff1f2' },
  { strong: '#4338ca', pale: '#eef2ff' },
  { strong: '#0f766e', pale: '#f0fdfa' }
];

const spineColor = (key) => {
  const s = String(key ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SPINE_COLORS[h % SPINE_COLORS.length];
};

const PAGE_SIZE = 10;

const SpineLabel = () => {
  const libraryId = useActiveLibraryId();

  const [copies, setCopies] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchCopies = async () => {
    try {
      if (!libraryId) return;

      const data = await getBookCopiesByLibrary(libraryId);
      setCopies(data || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  useEffect(() => {
    fetchCopies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const handlePrint = () => {
    if (selectedIds.length === 0) {
      Swal.fire('Error', 'Please select copies to print', 'error');
      return;
    }

    window.print();
  };

  const filteredCopies = copies.filter((copy) => {
    if (statusFilter && String(copy.availability_status || '').toLowerCase() !== statusFilter) return false;
    const keyword = search.toLowerCase();
    if (!keyword) return true;
    return (
      String(copy.title || copy.book_title || '').toLowerCase().includes(keyword) ||
      String(copy.accession_no || '').toLowerCase().includes(keyword) ||
      String(copy.barcode || '').toLowerCase().includes(keyword) ||
      String(copy.catalogue_no || '').toLowerCase().includes(keyword)
    );
  });

  const selectedCopies = copies.filter((copy) => selectedIds.includes(copy.id));

  const allSelected =
    filteredCopies.length > 0 &&
    filteredCopies.every((copy) => selectedIds.includes(copy.id));

  const totalPages = Math.max(1, Math.ceil(filteredCopies.length / PAGE_SIZE));
  const paginatedCopies = filteredCopies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="container-fluid mt-3">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 10mm;
            }

            body * {
              visibility: hidden;
            }

            .print-area, .print-area * {
              visibility: visible;
            }

            .print-area {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10mm;
              justify-items: center;
            }

            .no-print {
              display: none !important;
            }

            .spine-label {
              width: 100%;
              max-width: 60mm;
              margin: 0;
              page-break-inside: avoid;
            }
          }

          .spine-label, .spine-label * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .spine-label {
            width: 95px;
            min-height: 260px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
            margin: 8px;
            display: inline-flex;
            flex-direction: column;
            text-align: center;
            vertical-align: top;
            box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
          }

          .spine-cap {
            font-size: 10px;
            font-weight: 700;
            color: #fff;
            padding: 6px 4px;
            letter-spacing: .3px;
            word-break: break-all;
          }

          .spine-body { flex: 1; display: flex; align-items: center; justify-content: center; padding: 6px 0; }

          .spine-title {
            writing-mode: vertical-rl;
            text-orientation: mixed;
            font-weight: 700;
            font-size: 12px;
            max-height: 180px;
            overflow: hidden;
          }
        `}
      </style>

      <div className="no-print">
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon">
              <i className="fas fa-tag" />
            </div>
            <div>
              <h3 className="lib-page-header__title">Spine Labels</h3>
              <div className="lib-page-header__subtitle">Print spine labels for book copies</div>
            </div>
          </div>
          <div className="lib-page-header__actions">
            <button className="btn btn-primary" onClick={handlePrint}>
              Print Selected
            </button>
          </div>
        </div>

        <div className="card shadow-sm table-full-card">
          <div className="card-body">
            <div className="book-filter-row mb-3">
              <input
                type="text"
                className="book-search-input"
                placeholder="Book title, accession no, catalogue no..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_FILTER_OPTIONS}
                placeholder="All Status"
              />
              <button
                className="book-reset-btn"
                onClick={() => { setSearch(''); setStatusFilter(''); setSelectedIds([]); }}
              >
                Reset
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) =>
                          setSelectedIds(
                            e.target.checked ? filteredCopies.map((item) => item.id) : []
                          )
                        }
                      />
                      {/* <span className="ms-1">Select</span> */}
                    </th>
                    <th>#</th>
                    <th>Book</th>
                    <th>Accession</th>
                    <th>Catalogue</th>
                    <th>Location</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedCopies.map((copy, index) => (
                    <tr key={copy.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(copy.id)}
                          onChange={() => toggleSelect(copy.id)}
                        />
                      </td>
                      <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                      <td>{copy.title || copy.book_title || '-'}</td>
                      <td>{copy.accession_no || '-'}</td>
                      <td>{copy.catalogue_no || '-'}</td>
                      <td>
                        Rack: {copy.rack_no || '-'} | Shelf: {copy.shelf_no || '-'}
                      </td>
                    </tr>
                  ))}

                  {filteredCopies.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No copies found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredCopies.length}
                itemLabel="copies"
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="print-area">
        {selectedCopies.map((copy) => {
          const c = spineColor(copy.book_id ?? copy.title ?? copy.book_title);
          return (
            <div className="spine-label" key={copy.id} style={{ background: c.pale, borderColor: c.strong }}>
              <div className="spine-cap" style={{ background: c.strong }}>
                {copy.catalogue_no || copy.accession_no || '-'}
              </div>

              <div className="spine-body">
                <div className="spine-title" style={{ color: c.strong }}>
                  {copy.title || copy.book_title || 'Book'}
                </div>
              </div>

              <div className="spine-cap" style={{ background: c.strong }}>
                {copy.accession_no || '-'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SpineLabel;